import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { type AwardCriterionDto, WeightType } from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import { formatWeight } from "../../utils/displayFormatters";
import {
  effectiveWeight,
  formatWeightPercent,
  subWeightsValid,
  subWeightTotal,
} from "../../utils/awardCriteriaStatus";
import {
  criterionTypeLabelKey,
  ENVIRONMENTAL_AWARD_CRITERION_TYPES,
  isExemptionMarker,
} from "../../utils/publicProcurementOptions";
import { EmptyValue } from "./SummaryField";

interface AwardCriteriaWeightsTableProps {
  criteria: AwardCriterionDto[];
}

// t('Sum of requirements') t('Effective weight')

// The sub-criteria of one criterion, with their own 100 % total. Their weights are shares of the
// criterion, so they are shown next to the effective share of the whole procurement.
const SubCriteriaRows = ({ criterion }: { criterion: AwardCriterionDto }) => {
  const { t } = useTranslation();
  const subCriteria = criterion.subCriteria ?? [];
  const isValid = subWeightsValid(criterion);
  const total = subWeightTotal(criterion);

  return (
    <Box mt="2" pl="3" borderLeftWidth="2px" borderLeftColor="smBorder">
      {subCriteria.map((sub, index) => {
        const effective = effectiveWeight(criterion, sub);
        return (
          <Flex
            key={sub.id ?? index}
            justify="space-between"
            align="center"
            gap="3"
            fontSize="sm"
            py="1"
          >
            <Text minW="0">{sub.name || <EmptyValue text={t("Not set")} />}</Text>
            <Flex align="center" gap="3" flexShrink="0">
              {effective != null && (
                <Text color="smMuted" fontSize="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatWeightPercent(effective)} % {t("of the total")}
                </Text>
              )}
              <Text sx={{ fontVariantNumeric: "tabular-nums" }}>
                {sub.weight?.number != null ? `${sub.weight.number}%` : <EmptyValue text={t("Not set")} />}
              </Text>
            </Flex>
          </Flex>
        );
      })}
      <Flex
        data-testid="award-sub-criteria-total"
        data-valid={isValid ? "true" : "false"}
        justify="space-between"
        align="center"
        pt="1"
        mt="1"
        borderTopWidth="1px"
        borderTopColor="smBorder"
        fontSize="xs"
        fontWeight="semibold"
        color={isValid ? "smSuccess" : "smError"}
      >
        <Text>{t("Sum of requirements")}</Text>
        <Text sx={{ fontVariantNumeric: "tabular-nums" }}>{total}%</Text>
      </Flex>
    </Box>
  );
};

export const AwardCriteriaWeightsTable = ({ criteria: allCriteria }: AwardCriteriaWeightsTableProps) => {
  const { t } = useTranslation();
  // A § 5c exemption marker is state, not a weighted row — surface it via the justification block
  // (in AwardCriteriaSection), never as a row or in the 100 % total.
  const criteria = allCriteria.filter((c) => !isExemptionMarker(c.type));
  // The 100% total is only meaningful when every weight is percentage-typed.
  // The wizard only emits Percentage today, but the DTO type permits other
  // variants — guard explicitly so a future non-percentage weight cannot
  // sneak past as a misleading "n%" footer.
  const allPercentage = criteria.every(
    (c) => c.weight == null || c.weight.type === WeightType.Percentage,
  );
  const total = criteria.reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
  const isValid = allPercentage && total === 100;

  return (
    <Box borderWidth="1px" borderRadius="md" borderColor="smBorder" overflow="hidden">
      {criteria.map((c, index) => (
        <Box
          key={`${c.name ?? c.type}-${index}`}
          px="3"
          py="2"
          borderBottomWidth="1px"
          borderBottomColor="smBorder"
          _last={{ borderBottomWidth: 0 }}
        >
          <Flex justify="space-between" align="center" gap="3" fontSize="sm">
            <Flex align="center" gap="2" minW="0">
              <Text fontWeight="medium">{c.name || t(criterionTypeLabelKey(c.type))}</Text>
              {c.name ? (
                <Badge
                  flexShrink="0"
                  fontSize="2xs"
                  bg={ENVIRONMENTAL_AWARD_CRITERION_TYPES.includes(c.type) ? "smSuccessSoft" : "smBackgroundSecondary"}
                  color={ENVIRONMENTAL_AWARD_CRITERION_TYPES.includes(c.type) ? "smSuccess" : "smMuted"}
                >
                  {t(criterionTypeLabelKey(c.type))}
                </Badge>
              ) : null}
            </Flex>
            {formatWeight(c.weight) != null ? (
              <Text
                fontWeight="semibold"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {formatWeight(c.weight)}
              </Text>
            ) : (
              <EmptyValue text={t("Not set")} />
            )}
          </Flex>
          {!isTiptapContentEmpty(c.description) && (
            <Box color="smMuted" fontSize="sm" mt="1">
              <TipTap
                content={c.description}
                editable={false}
                hasBorder={false}
                padding="0"
                maxHeight=""
              />
            </Box>
          )}
          {(c.subCriteria ?? []).length > 0 && <SubCriteriaRows criterion={c} />}
        </Box>
      ))}
      <Flex
        data-testid="award-criteria-total"
        data-valid={isValid ? "true" : "false"}
        justify="space-between"
        align="center"
        px="3"
        py="2"
        bg={isValid ? "smSuccessSoft" : "smErrorSoft"}
        borderTopWidth="1px"
        borderTopColor={isValid ? "smSuccessSoftBorder" : "smErrorSoftBorder"}
      >
        <Text fontSize="sm" fontWeight="semibold" color={isValid ? "smSuccess" : "smError"}>
          {t("Total")}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="bold"
          color={isValid ? "smSuccess" : "smError"}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {allPercentage ? `${total}%` : total}
        </Text>
      </Flex>
    </Box>
  );
};
